import { Fragment, useEffect, useState } from "react";
import {
  Plus, FileDown, Trash2, Loader2, Receipt, X, Pencil, Search, Minus,
  ArrowLeft, ChevronRight, ChevronDown, Copy, Building2,
  Check, CheckCircle2, Clock, Mail, MessageSquare, AlertCircle, Phone, MessageCircle,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { pdfUrl } from "../../lib/pdfUrl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  loadDraft, saveDraft, clearDraft, DRAFT_RESUME_EVENT,
} from "../../lib/quotationDraft";
import { STANDARD_PRODUCTS } from "../../lib/productsCatalog";

const blankItem = {
  description: "", detail: "", hsn_code: "", gst_rate: 18, qty: 1, price: 0,
};

const DEFAULT_TERMS = [
  "Subject to Bangalore Jurisdiction.",
  "Goods once sold will not be taken back.",
  "Validity : 15 days from date of this offer.",
  "Replacement warranty : One year from the date of supply.",
  "Erection/Commng. : Actual, to provide Welder with Welding Machine & Electrodes to be provided at your end whenever required.",
  "Installation charges : Extra",
  "Transportation charges : Extra",
  "100% Advance Payment.",
];

const empty = {
  buyer_name: "", buyer_address: "", buyer_gstin: "",
  contact_person: "", contact_number: "",
  rfq_no: "", consignee: "", email: "", date: "",
  vehicle_make: "", vehicle_model: "", vehicle_remark: "", vehicle_location: "",
  items: [{ ...blankItem }],
  installation_charge: 0, transportation_charge: 0, discount_pct: 0,
  cgst_pct: 9, sgst_pct: 9,
  apply_igst: false, igst_pct: 18,
  po_completed: false,
  remarks: "",
  terms_text: DEFAULT_TERMS.join("\n"),
};

// True when the user has actually typed something (ignores the pre-filled
// defaults like date, GST %, default terms) — only then do we keep a draft.
function isMeaningful(f) {
  if (!f) return false;
  const txt = ["buyer_name", "buyer_address", "buyer_gstin", "contact_person",
    "contact_number", "rfq_no", "consignee", "email", "remarks",
    "vehicle_make", "vehicle_model", "vehicle_remark", "vehicle_location"];
  if (txt.some((k) => String(f[k] || "").trim())) return true;
  if (f.po_completed) return true;
  if ((f.items || []).some((it) =>
    String(it.description || "").trim() || String(it.detail || "").trim() ||
    String(it.hsn_code || "").trim() || Number(it.price) > 0)) return true;
  if (Number(f.installation_charge) > 0 || Number(f.transportation_charge) > 0 || Number(f.discount_pct) > 0) return true;
  return String(f.terms_text || "") !== DEFAULT_TERMS.join("\n");
}

// ---- Company-wise grouping -------------------------------------------------
// Quotations are grouped by buyer (company) name so 10 companies don't mean a
// 100-row flat list. Names are normalised (case / spaces / trailing dots) so
// "ABC Ltd" and "abc ltd." land in the same company.
const UNNAMED = "__unnamed__";
const companyKey = (name) =>
  String(name || "").trim().toLowerCase().replace(/\s+/g, " ").replace(/[.,;:\s]+$/, "");
const qTime = (q) => String(q.created_at || q.date || "");

function groupByCompany(list) {
  const map = new Map();
  for (const q of list) {
    const key = companyKey(q.buyer_name) || UNNAMED;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(q);
  }
  const groups = [];
  map.forEach((arr, key) => {
    arr.sort((a, b) => qTime(b).localeCompare(qTime(a)));   // newest first
    const latest = arr[0];
    groups.push({
      key,
      name: String(latest.buyer_name || "").trim() || "(No company name)",
      quotations: arr,
      latest,
      total: arr.reduce((sum, x) => sum + (Number(x.grand_total) || 0), 0),
    });
  });
  // Most recently quoted company first
  groups.sort((a, b) => qTime(b.latest).localeCompare(qTime(a.latest)));
  return groups;
}

const fmtDate = (d) => {
  const m = String(d || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (d || "");
};

function cleanPhone(p) {
  const digits = String(p || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function openGmail(email, e) {
  if (e && e.stopPropagation) e.stopPropagation();
  if (e && e.preventDefault) e.preventDefault();
  if (!email) return;
  const clean = String(email).trim();
  const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(clean)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

// Calculate how many days have elapsed since the quotation date
function getDaysElapsed(dateStr) {
  if (!dateStr) return null;
  const parts = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!parts) return null;
  const d = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today - d) / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

function renderReminderBadge(days, isPoCompleted) {
  if (isPoCompleted) return null;
  if (days === null || days === undefined) return null;
  const count = Math.max(0, days);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200"
      title={`${count} days reminder`}
    >
      <Clock className="w-2.5 h-2.5 text-amber-600" />
      {count} {count === 1 ? "day" : "days"} reminder
    </span>
  );
}

// Saved quotation -> form state (used by Edit and by "copy as new").
function quotationToForm(q) {
  return {
    buyer_name: q.buyer_name || "",
    buyer_address: q.buyer_address || "",
    buyer_gstin: q.buyer_gstin || "",
    contact_person: q.contact_person || "",
    contact_number: q.contact_number || "",
    rfq_no: q.rfq_no || "",
    consignee: q.consignee || "",
    email: q.email || "",
    date: q.date || new Date().toISOString().slice(0, 10),
    vehicle_make: q.vehicle_make || "",
    vehicle_model: q.vehicle_model || q.vehicle_model_no || "",
    vehicle_remark: q.vehicle_remark || q.vehicle_remarks || "",
    vehicle_location: q.vehicle_location || q.location || "",
    items: (q.items && q.items.length ? q.items : [blankItem]).map((it) => ({
      description: it.description || "",
      detail: it.detail || "",
      hsn_code: it.hsn_code || "",
      gst_rate: it.gst_rate ?? 18,
      qty: it.qty ?? 1,
      price: it.price ?? 0,
    })),
    installation_charge: q.installation_charge || 0,
    transportation_charge: q.transportation_charge || 0,
    discount_pct: q.discount_pct || 0,
    cgst_pct: q.cgst_pct ?? 9,
    sgst_pct: q.sgst_pct ?? 9,
    apply_igst: !!q.apply_igst,
    igst_pct: q.igst_pct || 18,
    po_completed: !!q.po_completed,
    remarks: q.remarks || "",
    terms_text: (q.terms && q.terms.length ? q.terms : DEFAULT_TERMS).join("\n"),
  };
}

function _money(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function calcTotals(form) {
  const items_subtotal = form.items.reduce(
    (s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0,
  );
  const installation = Number(form.installation_charge) || 0;
  const transportation = Number(form.transportation_charge) || 0;
  const pre_discount = items_subtotal + installation + transportation;
  const discount_pct = Number(form.discount_pct) || 0;
  const discount = pre_discount * discount_pct / 100;
  const subtotal = pre_discount - discount;
  let cgst = 0, sgst = 0, igst = 0;
  if (form.apply_igst) {
    igst = subtotal * (Number(form.igst_pct) || 0) / 100;
  } else {
    cgst = subtotal * (Number(form.cgst_pct) || 0) / 100;
    sgst = subtotal * (Number(form.sgst_pct) || 0) / 100;
  }
  const tax = cgst + sgst + igst;
  const grand = subtotal + tax;
  return { items_subtotal, installation, transportation, discount_pct, discount, subtotal, cgst, sgst, igst, tax, grand };
}

export default function EstimatedQuotationsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);   // company key
  const [expandedId, setExpandedId] = useState(null);             // open quotation row
  const [poStatusFilter, setPoStatusFilter] = useState("all");     // "all" | "Completed" | "Not Completed"
  const [remarkDialog, setRemarkDialog] = useState({ open: false, qid: null, text: "" });
  const [downloadingId, setDownloadingId] = useState(null);

  const downloadQuotationPdf = async (qt, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (e && e.preventDefault) e.preventDefault();
    const buyer = (qt?.buyer_name || form?.buyer_name || "").replace(/[\\/*?:"<>|]/g, "").trim().replace(/\s+/g, " ");
    const estNo = (qt?.est_no || "UEE-Quotation").replace(/[\\/*?:"<>|]/g, "").trim().replace(/\s+/g, " ");
    const filename = buyer ? `${estNo}-${buyer}.pdf` : `${estNo}.pdf`;

    if (qt?.id) setDownloadingId(qt.id);
    try {
      let blob = null;

      // 1. Try download endpoint via Axios
      if (qt?.id) {
        try {
          const res = await api.get(`/quotations/${qt.id}/download`, { responseType: "blob" });
          if (res?.data && res.data.size > 100) {
            blob = res.data;
          }
        } catch (apiErr) {
          console.warn("Direct API download endpoint failed, trying static URL fallback", apiErr);
        }
      }

      // 2. Fallback to direct static fetch if blob not yet obtained
      if (!blob) {
        const url = pdfUrl(qt);
        if (!url) throw new Error("PDF url not available");
        const res = await fetch(url);
        if (!res.ok) throw new Error("PDF download failed");
        blob = await res.blob();
      }

      // 3. Trigger download with exact filename
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
      toast.success(`Downloaded: ${filename}`);
    } catch (err) {
      console.warn("Direct blob download failed, opening in browser tab", err);
      if (qt?.id) {
        const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
        window.open(`${backend}/api/quotations/${qt.id}/download`, "_blank");
      } else if (qt?.pdf_url) {
        window.open(pdfUrl(qt), "_blank");
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const load = () => api.get("/quotations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const updateQuotationStatus = async (id, patch, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      setItems((prev) =>
        prev.map((q) => (q.id === id ? { ...q, ...patch } : q))
      );
      await api.patch(`/quotations/${id}`, patch);
      if (patch.po_completed !== undefined) {
        toast.success(patch.po_completed ? "PO Completed marked: OK ✔" : "PO Status marked: Not OK");
      } else if (patch.remarks !== undefined) {
        toast.success("Remarks updated");
      }
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const openRemarkDialog = (q, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setRemarkDialog({ open: true, qid: q.id, text: q.remarks || "" });
  };

  const saveRemarkDialog = async () => {
    if (!remarkDialog.qid) return;
    await updateQuotationStatus(remarkDialog.qid, { remarks: remarkDialog.text });
    setRemarkDialog({ open: false, qid: null, text: "" });
  };

  // ---- Gmail-style draft handling -------------------------------------
  // Load the saved draft back into the form and open the popup.
  const resumeDraft = () => {
    const d = loadDraft();
    if (!d) return false;
    setEditingId(d.editingId || null);
    setForm({ ...empty, ...d.form });
    setOpen(true);
    return true;
  };

  // Minimize = close the popup but KEEP the data as a draft (bar shows at
  // the bottom of every admin page, click to resume).
  const minimize = () => {
    if (isMeaningful(form)) {
      saveDraft({ form, editingId, open: false });
    } else {
      clearDraft();
    }
    setOpen(false);
  };

  // Discard = throw the draft away for good.
  const discard = () => {
    if (isMeaningful(form) &&
        !window.confirm("Draft discard kar dein? Saved data hat jaayega.")) return;
    clearDraft();
    setOpen(false);
    setEditingId(null);
    setForm(empty);
  };

  // On first load: bring any saved draft back into state (popup stays closed;
  // the draft bar offers "Resume"). `?resume=1` (from the bar on another
  // page) opens it straight away. Skipped when arriving from an RFQ.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from_rfq")) return;
    const d = loadDraft();
    if (!d) return;
    setEditingId(d.editingId || null);
    setForm({ ...empty, ...d.form });
    if (params.get("resume")) {
      setOpen(true);
      params.delete("resume");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    } else {
      // Popup is closed now, so mark the draft as minimized for the bar.
      saveDraft({ form: d.form, editingId: d.editingId || null, open: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Resume" clicked on the bar while already on this page.
  useEffect(() => {
    const onResume = () => { resumeDraft(); };
    window.addEventListener(DRAFT_RESUME_EVENT, onResume);
    return () => window.removeEventListener(DRAFT_RESUME_EVENT, onResume);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save while the popup is open (like Gmail's "Draft saved").
  useEffect(() => {
    if (!open) return;
    if (!isMeaningful(form)) return;
    saveDraft({ form, editingId, open: true });
  }, [form, editingId, open]);

  const openNew = (prefill = {}) => {
    // A meaningful draft already exists → offer to resume it instead of
    // silently losing it.
    const existing = loadDraft();
    if (existing && isMeaningful(existing.form)) {
      const resume = window.confirm(
        "Ek saved draft maujood hai.\n\nOK = Draft resume karo\nCancel = Naya form (purana draft hat jaayega)"
      );
      if (resume && resumeDraft()) return;
      clearDraft();
    }
    setEditingId(null);
    setForm({ ...empty, date: new Date().toISOString().slice(0, 10), ...prefill });
    setOpen(true);
  };

  // New quotation for an existing company: copy everything from an earlier
  // quotation (company details, devices, rates, taxes, terms) with today's
  // date — only device name / rate need to be changed.
  const openFromQuotation = (src) => {
    openNew({
      ...quotationToForm(src),
      date: new Date().toISOString().slice(0, 10),
      rfq_no: "",
    });
  };

  const openEdit = (q) => {
    setEditingId(q.id);
    setForm(quotationToForm(q));
    setOpen(true);
  };

  // Allow opening pre-filled from RFQ via query param ?from_rfq=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rfqId = params.get("from_rfq");
    if (!rfqId) return;
    api.get(`/rfq`).then((r) => {
      const rfq = (r.data || []).find((x) => x.id === rfqId);
      if (!rfq) return;
      openNew({
        buyer_name: rfq.company || rfq.name,
        contact_person: rfq.name,
        contact_number: rfq.mobile,
        email: rfq.email,
        rfq_no: `RFQ-${rfq.id.slice(0, 8).toUpperCase()}`,
        items: [{
          ...blankItem,
          description: rfq.product_name || rfq.requirement?.slice(0, 80) || "",
          qty: Number(rfq.quantity) || 1,
        }],
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the open company disappears (e.g. its last quotation was deleted or
  // renamed), go back to the company list.
  useEffect(() => {
    if (!selectedCompany || items.length === 0) return;
    const still = items.some((x) => (companyKey(x.buyer_name) || UNNAMED) === selectedCompany);
    if (!still) setSelectedCompany(null);
  }, [items, selectedCompany]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const updateItem = (idx, patch) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], ...patch };
    setForm({ ...form, items });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { ...blankItem }] });
  const removeItem = (idx) => setForm({
    ...form, items: form.items.filter((_, i) => i !== idx),
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const terms = (form.terms_text || "")
        .split("\n")
        .map((t) => t.trim())
        .filter(Boolean);
      const payload = {
        buyer_name: form.buyer_name,
        buyer_address: form.buyer_address,
        buyer_gstin: form.buyer_gstin || "",
        contact_person: form.contact_person,
        contact_number: form.contact_number,
        rfq_no: form.rfq_no,
        consignee: form.consignee,
        email: form.email,
        date: form.date,
        vehicle_make: (form.vehicle_make || "").trim(),
        vehicle_model: (form.vehicle_model || "").trim(),
        vehicle_remark: (form.vehicle_remark || "").trim(),
        vehicle_location: (form.vehicle_location || "").trim(),
        items: form.items.map((it, i) => ({
          sl_no: String(i + 1),
          description: it.description,
          detail: it.detail || "",
          hsn_code: it.hsn_code || "",
          gst_rate: Number(it.gst_rate) || 0,
          qty: Number(it.qty) || 0,
          price: Number(it.price) || 0,
        })),
        installation_charge: Number(form.installation_charge) || 0,
        transportation_charge: Number(form.transportation_charge) || 0,
        discount_pct: Number(form.discount_pct) || 0,
        cgst_pct: form.apply_igst ? 0 : (Number(form.cgst_pct) || 0),
        sgst_pct: form.apply_igst ? 0 : (Number(form.sgst_pct) || 0),
        apply_igst: !!form.apply_igst,
        igst_pct: form.apply_igst ? (Number(form.igst_pct) || 0) : 0,
        po_completed: !!form.po_completed,
        remarks: form.remarks || "",
        terms,
      };
      if (editingId) {
        const { data } = await api.put(`/quotations/${editingId}`, payload);
        toast.success(`Quotation ${data.est_no} updated`);
      } else {
        const { data } = await api.post("/quotations", payload);
        toast.success(`Quotation ${data.est_no} created`);
      }
      clearDraft();
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (q) => {
    if (!window.confirm(`Delete quotation ${q.est_no}?`)) return;
    try {
      await api.delete(`/quotations/${q.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const t = calcTotals(form);

  // Case-insensitive client-side search across EST No, company, contact,
  // consignee, address and device names. A company is shown when ANY of its
  // quotations matches.
  const query = searchQuery.trim().toLowerCase();
  const matches = (it) => {
    const haystack = [
      it.est_no, it.buyer_name, it.contact_person, it.contact_number,
      it.consignee, it.buyer_address,
      ...((it.items || []).map((x) => x.description)),
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(query);
  };

  const statusMatch = (it) => {
    if (poStatusFilter === "Completed") return Boolean(it.po_completed);
    if (poStatusFilter === "Not Completed") return !it.po_completed;
    return true;
  };

  const allGroups = groupByCompany(items);
  const groups = allGroups
    .map((g) => {
      const filteredQuotes = g.quotations.filter((it) => matches(it) && statusMatch(it));
      if (filteredQuotes.length === 0) return null;
      return {
        ...g,
        quotations: filteredQuotes,
        latest: filteredQuotes[0],
      };
    })
    .filter(Boolean);

  const activeGroup = selectedCompany ? allGroups.find((g) => g.key === selectedCompany) : null;

  return (
    <div data-testid="admin-quotations-builder-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Sales</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-3">
            <Receipt className="w-6 h-6 text-amber-500" /> Estimated Quotations
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            GST quotations matching the physical paper format — auto-numbered (UEE-26301).
          </p>
        </div>
        <Button data-testid="new-quotation-btn" onClick={() => openNew()} className="rounded-none btn-amber font-semibold h-10">
          <Plus className="w-4 h-4 mr-2" /> New Quotation
        </Button>
      </div>

      {!activeGroup && (
        <>
          {/* Search & PO Status Filter bar */}
          <div className="mb-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Company, EST No, Contact or Device…"
                className="pl-9 pr-9 rounded-none h-10 text-xs"
                data-testid="quotation-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                  data-testid="quotation-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
              {["all", "Completed", "Not Completed"].map((st) => (
                <button
                  key={st}
                  onClick={() => setPoStatusFilter(st)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                    poStatusFilter === st
                      ? "bg-slate-900 text-white shadow-xs"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {st === "all" ? "All PO Statuses" : st}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="company-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left p-3 font-semibold">Company</th>
                  <th className="text-center p-3 font-semibold">Quotations</th>
                  <th className="text-left p-3 font-semibold">Email ID</th>
                  <th className="text-left p-3 font-semibold">Date &amp; Reminder</th>
                  <th className="text-center p-3 font-semibold">PO Status</th>
                  <th className="text-left p-3 font-semibold">Contact Person &amp; No.</th>
                  <th className="p-3 w-8" />
                </tr>
              </thead>
              <tbody>
                {groups.map((g, i) => (
                  <tr
                    key={g.key}
                    onClick={() => { setSelectedCompany(g.key); setExpandedId(null); }}
                    className="border-t border-slate-200 cursor-pointer hover:bg-amber-50/50"
                    data-testid={`company-row-${i}`}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2 font-semibold">
                        <Building2 className="w-4 h-4 text-amber-500 shrink-0" />
                        <span>{g.name}</span>
                      </div>
                      <div className="text-xs text-slate-500 font-normal mt-0.5 max-w-[320px] truncate">
                        {g.latest.buyer_address || "—"}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-block min-w-[2rem] px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-800 rounded-full">
                        {g.quotations.length}
                      </span>
                    </td>
                    <td className="p-3 text-xs" onClick={(e) => e.stopPropagation()}>
                      {g.latest.email ? (
                        <a
                          href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(g.latest.email.trim())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => openGmail(g.latest.email, e)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-800 hover:underline max-w-[240px] truncate"
                          title={`Click to compose in Gmail for ${g.latest.email}`}
                        >
                          <Mail className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                          <span className="truncate">{g.latest.email}</span>
                        </a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="font-mono text-xs font-medium">{fmtDate(g.latest.date)}</div>
                      <div className="mt-1">
                        {renderReminderBadge(getDaysElapsed(g.latest.date), g.latest.po_completed)}
                      </div>
                    </td>
                    <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={g.latest.po_completed ? "Completed" : "Not Completed"}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateQuotationStatus(g.latest.id, { po_completed: e.target.value === "Completed" }, e);
                        }}
                        className={`text-xs font-bold px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none transition-colors ${
                          g.latest.po_completed
                            ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                            : "bg-slate-100 text-slate-700 border-slate-300"
                        }`}
                      >
                        <option value="Completed">Completed</option>
                        <option value="Not Completed">Not Completed</option>
                      </select>
                    </td>
                    <td className="p-3 text-xs" onClick={(e) => e.stopPropagation()}>
                      {g.latest.contact_person || g.latest.contact_number ? (
                        <div>
                          <div className="font-semibold text-slate-900 truncate max-w-[200px]" title={g.latest.contact_person}>
                            {g.latest.contact_person || "—"}
                          </div>
                          {g.latest.contact_number && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <a
                                href={`tel:${g.latest.contact_number}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-amber-700 font-mono bg-slate-100 hover:bg-amber-100 px-1.5 py-0.5 rounded transition-colors"
                                title={`Call ${g.latest.contact_number}`}
                              >
                                <Phone className="w-3 h-3 text-amber-600 shrink-0" />
                                <span>{g.latest.contact_number}</span>
                              </a>
                              <a
                                href={`https://wa.me/${cleanPhone(g.latest.contact_number)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded transition-colors"
                                title={`WhatsApp chat with ${g.latest.contact_number}`}
                              >
                                <MessageCircle className="w-3 h-3 text-emerald-600 shrink-0" />
                                <span>WA</span>
                              </a>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && (
              <div className="p-12 text-center text-slate-500 text-sm">
                No quotations yet. Click <strong>New Quotation</strong> to build one — or from a
                RFQ row click "Convert to Quotation".
              </div>
            )}
            {items.length > 0 && groups.length === 0 && (
              <div className="p-8 text-center text-slate-500 text-sm" data-testid="quotation-search-empty">
                No company matches <span className="font-mono font-semibold">"{searchQuery}"</span>.
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="ml-2 underline hover:text-slate-700"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {activeGroup && (
        <div data-testid="company-detail">
          <button
            type="button"
            onClick={() => { setSelectedCompany(null); setExpandedId(null); }}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-3"
            data-testid="company-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> All Companies
          </button>

          <div className="bg-white border border-slate-200 p-5 mb-3 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="overline mb-1">Company</div>
              <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-500" /> {activeGroup.name}
              </h2>
              {activeGroup.latest.buyer_address && (
                <div className="text-xs text-slate-600 mt-1 whitespace-pre-line">{activeGroup.latest.buyer_address}</div>
              )}
              <div className="text-xs text-slate-500 mt-1">
                {[
                  activeGroup.latest.buyer_gstin && `GSTIN: ${activeGroup.latest.buyer_gstin}`,
                  activeGroup.latest.contact_person,
                  activeGroup.latest.contact_number,
                ].filter(Boolean).join("  ·  ")}
              </div>
              <div className="text-xs text-slate-700 mt-2 font-semibold" data-testid="company-summary">
                {activeGroup.quotations.length} quotation{activeGroup.quotations.length === 1 ? "" : "s"} · Total ₹ {_money(activeGroup.total)}
              </div>
            </div>
            <Button
              data-testid="company-new-quotation-btn"
              onClick={() => openFromQuotation(activeGroup.latest)}
              className="rounded-none btn-amber font-semibold h-10"
            >
              <Plus className="w-4 h-4 mr-2" /> New Quotation for this company
            </Button>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Naya quotation pichle quotation (<span className="font-mono font-semibold">{activeGroup.latest.est_no}</span>) ki
            copy se khulega — company details aur devices pehle se bhare honge, bas device name aur rate badlo.
          </p>

          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left p-3 font-semibold w-10">No.</th>
                  <th className="text-left p-3 font-semibold">EST No</th>
                  <th className="text-left p-3 font-semibold">Date &amp; Reminder</th>
                  <th className="text-left p-3 font-semibold">Email</th>
                  <th className="text-left p-3 font-semibold">Devices</th>
                  <th className="text-center p-3 font-semibold">PO Status</th>
                  <th className="text-left p-3 font-semibold">Contact Person &amp; No.</th>
                  <th className="text-right p-3 font-semibold">Grand Total (₹)</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeGroup.quotations.filter(statusMatch).map((qt, idx) => {
                  const serial = activeGroup.quotations.length - idx;   // oldest = 1
                  const isOpen = expandedId === qt.id;
                  const devices = (qt.items || []).map((x) => x.description).filter(Boolean).join(", ");
                  return (
                    <Fragment key={qt.id}>
                      <tr
                        onClick={() => setExpandedId(isOpen ? null : qt.id)}
                        className={`border-t border-slate-200 cursor-pointer hover:bg-amber-50/50 ${isOpen ? "bg-amber-50/40" : ""}`}
                        data-testid={`quotation-row-${qt.id}`}
                      >
                        <td className="p-3 font-mono text-xs font-bold text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            {serial}
                          </span>
                        </td>
                        <td className="p-3 font-mono text-xs font-bold">{qt.est_no}</td>
                        <td className="p-3">
                          <div className="font-mono text-xs font-medium">{fmtDate(qt.date)}</div>
                          <div className="mt-1">
                            {renderReminderBadge(getDaysElapsed(qt.date), qt.po_completed)}
                          </div>
                        </td>
                        <td className="p-3 text-xs" onClick={(e) => e.stopPropagation()}>
                          {qt.email ? (
                            <a
                              href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(qt.email.trim())}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => openGmail(qt.email, e)}
                              className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-800 hover:underline max-w-[150px] truncate"
                              title={`Click to compose in Gmail for ${qt.email}`}
                            >
                              <Mail className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                              <span className="truncate">{qt.email}</span>
                            </a>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-3 text-xs max-w-[220px]">
                          <div className="truncate" title={devices}>{devices || "—"}</div>
                        </td>
                        <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={qt.po_completed ? "Completed" : "Not Completed"}
                            onChange={(e) => {
                              e.stopPropagation();
                              updateQuotationStatus(qt.id, { po_completed: e.target.value === "Completed" }, e);
                            }}
                            className={`text-xs font-bold px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none transition-colors ${
                              qt.po_completed
                                ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                : "bg-slate-100 text-slate-700 border-slate-300"
                            }`}
                          >
                            <option value="Completed">Completed</option>
                            <option value="Not Completed">Not Completed</option>
                          </select>
                        </td>
                        <td className="p-3 text-xs" onClick={(e) => e.stopPropagation()}>
                          {qt.contact_person || qt.contact_number ? (
                            <div>
                              <div className="font-semibold text-slate-900 truncate max-w-[180px]" title={qt.contact_person}>
                                {qt.contact_person || "—"}
                              </div>
                              {qt.contact_number && (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <a
                                    href={`tel:${qt.contact_number}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-amber-700 font-mono bg-slate-100 hover:bg-amber-100 px-1.5 py-0.5 rounded transition-colors"
                                    title={`Call ${qt.contact_number}`}
                                  >
                                    <Phone className="w-3 h-3 text-amber-600 shrink-0" />
                                    <span>{qt.contact_number}</span>
                                  </a>
                                  <a
                                    href={`https://wa.me/${cleanPhone(qt.contact_number)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded transition-colors"
                                    title={`WhatsApp chat with ${qt.contact_number}`}
                                  >
                                    <MessageCircle className="w-3 h-3 text-emerald-600 shrink-0" />
                                    <span>WA</span>
                                  </a>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-3 text-right font-mono font-semibold">{_money(qt.grand_total)}</td>
                        <td className="p-3 text-right space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => openEdit(qt)}
                            className="inline-flex p-2 hover:bg-amber-50 text-amber-700"
                            data-testid={`edit-quotation-${qt.id}`}
                            title="Edit & regenerate PDF"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openFromQuotation(qt)}
                            className="inline-flex p-2 hover:bg-sky-50 text-sky-700"
                            data-testid={`copy-quotation-${qt.id}`}
                            title="Is quotation ki copy se naya banao"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          {qt.pdf_url && (
                            <button
                              type="button"
                              onClick={(e) => downloadQuotationPdf(qt, e)}
                              disabled={downloadingId === qt.id}
                              className="inline-flex p-2 hover:bg-slate-100 disabled:opacity-50 text-slate-700"
                              data-testid={`qpdf-${qt.id}`}
                              title={`Download ${qt.est_no ? `${qt.est_no}-` : "UEE-"}${qt.buyer_name || "Customer"}.pdf`}
                            >
                              {downloadingId === qt.id ? (
                                <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                              ) : (
                                <FileDown className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button onClick={() => del(qt)} className="p-2 hover:bg-red-50 text-red-600"
                                  data-testid={`delete-quotation-${qt.id}`} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/70" data-testid={`quotation-detail-${qt.id}`}>
                          <td colSpan={9} className="p-4">
                            <table className="w-full text-xs bg-white border border-slate-200">
                              <thead className="bg-slate-100 uppercase tracking-wider">
                                <tr>
                                  <th className="text-left p-2 w-10">Sl</th>
                                  <th className="text-left p-2">Device / Description</th>
                                  <th className="text-left p-2">HSN</th>
                                  <th className="text-right p-2">Qty</th>
                                  <th className="text-right p-2">Rate (₹)</th>
                                  <th className="text-right p-2">Amount (₹)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(qt.items || []).map((it, k) => (
                                  <tr key={k} className="border-t border-slate-100">
                                    <td className="p-2 font-mono">{it.sl_no || k + 1}</td>
                                    <td className="p-2">
                                      <div className="font-semibold">{it.description}</div>
                                      {it.detail && <div className="text-slate-500 whitespace-pre-line">{it.detail}</div>}
                                    </td>
                                    <td className="p-2 font-mono">{it.hsn_code || "—"}</td>
                                    <td className="p-2 text-right font-mono">{it.qty}</td>
                                    <td className="p-2 text-right font-mono">{_money(it.price)}</td>
                                    <td className="p-2 text-right font-mono">{_money(it.amount ?? (Number(it.qty) || 0) * (Number(it.price) || 0))}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="mt-3 ml-auto max-w-xs text-xs space-y-1">
                              {Number(qt.installation_charge) > 0 && (
                                <div className="flex justify-between"><span>Installation Charges</span><span className="font-mono">{_money(qt.installation_charge)}</span></div>
                              )}
                              {Number(qt.transportation_charge) > 0 && (
                                <div className="flex justify-between"><span>Transportation Charges</span><span className="font-mono">{_money(qt.transportation_charge)}</span></div>
                              )}
                              {Number(qt.discount_amount) > 0 && (
                                <div className="flex justify-between"><span>Discount ({qt.discount_pct}%)</span><span className="font-mono">- {_money(qt.discount_amount)}</span></div>
                              )}
                              <div className="flex justify-between"><span>Taxable value</span><span className="font-mono">{_money(qt.subtotal)}</span></div>
                              <div className="flex justify-between"><span>GST</span><span className="font-mono">{_money(qt.total_tax)}</span></div>
                              <div className="flex justify-between font-bold text-sm border-t border-slate-300 pt-1">
                                <span>Grand Total</span><span className="font-mono">₹ {_money(qt.grand_total)}</span>
                              </div>
                            </div>
                            {qt.remarks && (
                              <div className="mt-3 p-2.5 bg-amber-50/70 border border-amber-200/80 rounded text-xs text-slate-700 flex items-start gap-2 max-w-xl">
                                <MessageSquare className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                <div>
                                  <span className="font-semibold text-amber-900">Remarks: </span>
                                  {qt.remarks}
                                </div>
                              </div>
                            )}
                            {(qt.vehicle_make || qt.vehicle_model || qt.vehicle_location || qt.vehicle_remark) && (
                              <div className="mt-3 p-2.5 bg-blue-50/70 border border-blue-200/80 rounded text-xs text-slate-800 max-w-xl">
                                <span className="font-bold text-blue-900 uppercase tracking-wider text-[11px] block mb-1">
                                  Vehicle Details:
                                </span>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                                  {qt.vehicle_make && <div><span className="text-slate-500 font-medium">Make:</span> <span className="font-semibold">{qt.vehicle_make}</span></div>}
                                  {qt.vehicle_model && <div><span className="text-slate-500 font-medium">Model No:</span> <span className="font-semibold">{qt.vehicle_model}</span></div>}
                                  {qt.vehicle_location && <div><span className="text-slate-500 font-medium">Location:</span> <span className="font-semibold">{qt.vehicle_location}</span></div>}
                                  {qt.vehicle_remark && <div><span className="text-slate-500 font-medium">Remark:</span> <span className="font-semibold">{qt.vehicle_remark}</span></div>}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (v) setOpen(true); else minimize(); }}>
        <DialogContent
          className="max-w-5xl rounded-none max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle className="font-heading">
                {editingId ? "Edit Estimated Quotation" : "New Estimated Quotation"}
              </DialogTitle>
              <button
                type="button"
                onClick={minimize}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-300 hover:border-slate-500 px-2.5 py-1"
                data-testid="quotation-minimize-btn"
                title="Minimize — data draft mein save rahega"
              >
                <Minus className="w-3.5 h-3.5" /> Minimize
              </button>
            </div>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5 mt-2" data-testid="quotation-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Buyer (Bill to) — Name *</Label>
                <Input required value={form.buyer_name} onChange={setField("buyer_name")} className="rounded-none"
                       data-testid="quotation-buyer-name" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Buyer Address</Label>
                <Textarea rows={2} value={form.buyer_address} onChange={setField("buyer_address")} className="rounded-none" />
              </div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Contact Person</Label>
                <Input value={form.contact_person} onChange={setField("contact_person")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Contact Number</Label>
                <Input value={form.contact_number} onChange={setField("contact_number")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Email</Label>
                <Input value={form.email} onChange={setField("email")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">RFQ No (optional)</Label>
                <Input value={form.rfq_no} onChange={setField("rfq_no")} className="rounded-none" /></div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Buyer GSTIN / UIN</Label>
                <Input value={form.buyer_gstin} onChange={setField("buyer_gstin")} className="rounded-none"
                       data-testid="quotation-buyer-gstin" placeholder="29ABCDE1234F1Z5" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Consignee (Ship to)</Label>
                <Textarea rows={2} value={form.consignee} onChange={setField("consignee")} className="rounded-none" />
              </div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Date *</Label>
                <Input type="date" required value={form.date} onChange={setField("date")} className="rounded-none" /></div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">PO Status</Label>
                <select
                  id="quotation-po-completed"
                  value={form.po_completed ? "Completed" : "Not Completed"}
                  onChange={(e) => setForm({ ...form, po_completed: e.target.value === "Completed" })}
                  className={`w-full h-10 text-xs font-bold px-3 py-1.5 rounded-none border cursor-pointer focus:outline-none mt-1 transition-colors ${
                    form.po_completed
                      ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                      : "bg-slate-50 text-slate-700 border-slate-300"
                  }`}
                >
                  <option value="Completed">Completed</option>
                  <option value="Not Completed">Not Completed</option>
                </select>
              </div>
            </div>

            {/* Vehicle Details (Optional) — Only printed on PDF if filled */}
            <div className="border border-blue-200 p-4 bg-blue-50/40 space-y-3">
              <div className="border-b border-blue-200/80 pb-2">
                <span className="text-xs uppercase tracking-wider font-bold text-blue-950 flex items-center gap-1.5">
                  🚗 Vehicle Details (Optional)
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Make</Label>
                  <Input
                    value={form.vehicle_make || ""}
                    onChange={setField("vehicle_make")}
                    placeholder="e.g. Tata, Mahindra, Ashok Leyland"
                    className="rounded-none mt-1 text-xs bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Model No</Label>
                  <Input
                    value={form.vehicle_model || ""}
                    onChange={setField("vehicle_model")}
                    placeholder="e.g. 1618, Prima, Bolero"
                    className="rounded-none mt-1 text-xs bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Location</Label>
                  <Input
                    value={form.vehicle_location || ""}
                    onChange={setField("vehicle_location")}
                    placeholder="e.g. Bangalore Yard, Site A"
                    className="rounded-none mt-1 text-xs bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Remark</Label>
                  <Input
                    value={form.vehicle_remark || ""}
                    onChange={setField("vehicle_remark")}
                    placeholder="e.g. Chassis / Engine / Spec Note"
                    className="rounded-none mt-1 text-xs bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Items grid */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-bold uppercase tracking-wider text-sm">Items</h3>
                <div className="flex items-center gap-2">
                  <select
                    value=""
                    onChange={(e) => {
                      const selected = STANDARD_PRODUCTS.find((p) => p.name === e.target.value);
                      if (selected) {
                        setForm({
                          ...form,
                          items: [
                            ...form.items,
                            {
                              ...blankItem,
                              description: selected.name,
                              detail: selected.description,
                              hsn_code: selected.hsn_code,
                            },
                          ],
                        });
                      }
                    }}
                    className="text-xs bg-white border border-slate-300 text-slate-700 rounded h-8 px-2 font-medium focus:ring-1 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="">⚡ + Add Standard Product...</option>
                    {STANDARD_PRODUCTS.map((prod) => (
                      <option key={prod.name} value={prod.name}>
                        {prod.name} (HSN: {prod.hsn_code})
                      </option>
                    ))}
                  </select>
                  <Button type="button" variant="outline" onClick={addItem} className="rounded-none h-8" data-testid="add-item-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add row
                  </Button>
                </div>
              </div>
              <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-white text-xs uppercase">
                    <tr>
                      <th className="p-2 text-left w-10">#</th>
                      <th className="p-2 text-left">Product Description</th>
                      <th className="p-2 text-left w-24">HSN</th>
                      <th className="p-2 text-right w-20">GST %</th>
                      <th className="p-2 text-right w-20">Qty</th>
                      <th className="p-2 text-right w-28">Price</th>
                      <th className="p-2 text-right w-28">Amount</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const amt = (Number(it.qty) || 0) * (Number(it.price) || 0);
                      return (
                        <tr key={i} className="border-t border-slate-200 align-top">
                          <td className="p-2 font-mono text-xs pt-3">{i + 1}</td>
                          <td className="p-1">
                            <select
                              value={STANDARD_PRODUCTS.some((p) => p.name === it.description) ? it.description : ""}
                              onChange={(e) => {
                                const selected = STANDARD_PRODUCTS.find((p) => p.name === e.target.value);
                                if (selected) {
                                  updateItem(i, {
                                    description: selected.name,
                                    detail: selected.description,
                                    hsn_code: selected.hsn_code,
                                  });
                                }
                              }}
                              className="w-full text-xs font-semibold bg-blue-50/70 hover:bg-blue-50 border border-blue-200 text-blue-900 rounded px-2 py-1 mb-1 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                              title="Select standard product to auto-fill details and HSN code"
                            >
                              <option value="">⚡ Select Standard Product...</option>
                              {STANDARD_PRODUCTS.map((prod) => (
                                <option key={prod.name} value={prod.name}>
                                  {prod.name} (HSN: {prod.hsn_code})
                                </option>
                              ))}
                            </select>
                            <Input
                              value={it.description}
                              required
                              placeholder="Product name (e.g. AFDSS Control Unit)"
                              onChange={(e) => {
                                const val = e.target.value;
                                const matched = STANDARD_PRODUCTS.find((p) => p.name.toLowerCase() === val.trim().toLowerCase());
                                if (matched) {
                                  updateItem(i, {
                                    description: val,
                                    detail: matched.description,
                                    hsn_code: matched.hsn_code,
                                  });
                                } else {
                                  updateItem(i, { description: val });
                                }
                              }}
                              className="rounded-none h-9 text-xs font-medium"
                              data-testid={`item-desc-${i}`}
                            />
                            <Textarea
                              value={it.detail}
                              placeholder="Description / Specs (auto-filled, editable)…"
                              onChange={(e) => updateItem(i, { detail: e.target.value })}
                              className="rounded-none mt-1 text-xs"
                              rows={2}
                              data-testid={`item-detail-${i}`}
                            />
                          </td>
                          <td className="p-1">
                            <Input value={it.hsn_code} onChange={(e) => updateItem(i, { hsn_code: e.target.value })} className="rounded-none h-9" />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="1" min="0" value={it.gst_rate} onChange={(e) => updateItem(i, { gst_rate: e.target.value })} className="rounded-none h-9 text-right font-mono" data-testid={`item-gst-${i}`} />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="1" min="1" value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })} className="rounded-none h-9 text-right font-mono" data-testid={`item-qty-${i}`} />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="any" min="0" value={it.price} onChange={(e) => updateItem(i, { price: e.target.value })} className="rounded-none h-9 text-right font-mono" data-testid={`item-price-${i}`} />
                          </td>
                          <td className="p-2 text-right font-mono pt-3">{_money(amt)}</td>
                          <td className="p-1 text-right pt-2">
                            {form.items.length > 1 && (
                              <button type="button" onClick={() => removeItem(i)} className="p-1 text-red-600 hover:bg-red-50">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Charges & Discount */}
            <div className="grid md:grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Installation Charges (₹)</Label>
                <Input type="number" step="0.01" value={form.installation_charge || ""}
                  onChange={setField("installation_charge")} className="rounded-none"
                  data-testid="installation-charge" placeholder="₹ 0.00" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Transportation Charges (₹)</Label>
                <Input type="number" step="0.01" value={form.transportation_charge || ""}
                  onChange={setField("transportation_charge")} className="rounded-none"
                  data-testid="transportation-charge" placeholder="₹ 0.00" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Discount (%)</Label>
                <Input type="number" step="0.01" min="0" max="100" value={form.discount_pct || ""}
                  onChange={setField("discount_pct")} className="rounded-none"
                  data-testid="discount-pct" placeholder="0" /></div>
            </div>

            {/* GST mode */}
            <div className="border border-slate-200 p-3 bg-slate-50">
              <div className="flex items-center gap-3 mb-3">
                <Checkbox
                  id="apply-igst"
                  checked={!!form.apply_igst}
                  onCheckedChange={(v) => setForm({ ...form, apply_igst: !!v })}
                  data-testid="apply-igst-checkbox"
                />
                <Label htmlFor="apply-igst" className="text-xs uppercase tracking-wider font-semibold cursor-pointer">
                  Apply IGST (inter-state) — disables CGST/SGST
                </Label>
              </div>
              {form.apply_igst ? (
                <div className="grid md:grid-cols-3 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">IGST %</Label>
                    <Input type="number" step="0.01" value={form.igst_pct} onChange={setField("igst_pct")}
                           className="rounded-none" data-testid="igst-pct" /></div>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">CGST %</Label>
                    <Input type="number" step="0.01" value={form.cgst_pct} onChange={setField("cgst_pct")} className="rounded-none" /></div>
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">SGST %</Label>
                    <Input type="number" step="0.01" value={form.sgst_pct} onChange={setField("sgst_pct")} className="rounded-none" /></div>
                </div>
              )}
            </div>

            {/* Totals preview */}
            <div className="bg-slate-50 border border-slate-200 p-4" data-testid="quotation-totals-preview">
              <div className="grid grid-cols-2 gap-x-12 gap-y-1 text-sm font-mono">
                <div>Items Subtotal</div><div className="text-right">{_money(t.items_subtotal)}</div>
                {t.installation > 0 && (<><div>Installation Charges</div><div className="text-right">{_money(t.installation)}</div></>)}
                {t.transportation > 0 && (<><div>Transportation Charges</div><div className="text-right">{_money(t.transportation)}</div></>)}
                {t.discount_pct > 0 && (
                  <>
                    <div>Discount @ {t.discount_pct}%</div>
                    <div className="text-right text-red-600">- {_money(t.discount)}</div>
                  </>
                )}
                <div className="font-semibold">Subtotal (taxable)</div><div className="text-right font-semibold">{_money(t.subtotal)}</div>
                {form.apply_igst ? (
                  <><div>IGST @ {form.igst_pct}%</div><div className="text-right">{_money(t.igst)}</div></>
                ) : (
                  <>
                    <div>CGST @ {form.cgst_pct}%</div><div className="text-right">{_money(t.cgst)}</div>
                    <div>SGST @ {form.sgst_pct}%</div><div className="text-right">{_money(t.sgst)}</div>
                  </>
                )}
                <div className="font-bold text-base pt-1 border-t border-slate-300">Grand Total</div>
                <div className="text-right font-bold text-base pt-1 border-t border-slate-300">₹ {_money(t.grand)}</div>
              </div>
            </div>

            {/* Editable Terms & Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">
                  Terms &amp; Conditions (one per line — leave blank to omit section)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-none h-7 text-xs"
                  onClick={() => setForm({ ...form, terms_text: DEFAULT_TERMS.join("\n") })}
                  data-testid="reset-terms-btn"
                >
                  Reset to defaults
                </Button>
              </div>
              <Textarea
                rows={7}
                value={form.terms_text}
                onChange={setField("terms_text")}
                className="rounded-none font-mono text-xs"
                data-testid="quotation-terms"
                placeholder="One term per line…"
              />
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              {isMeaningful(form) && (
                <span className="mr-auto text-xs text-slate-500" data-testid="quotation-autosave-note">
                  Draft auto-saved — page badal sakte ho, data safe rahega
                </span>
              )}
              <Button type="button" variant="outline" onClick={discard} className="rounded-none text-red-600" data-testid="quotation-discard-btn">Discard</Button>
              <Button type="button" variant="outline" onClick={minimize} className="rounded-none">Minimize</Button>
              <Button data-testid="quotation-submit" type="submit" disabled={busy} className="rounded-none btn-amber font-semibold">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingId ? "Save & Regenerate PDF" : "Generate Quotation PDF"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inline Remarks Edit Dialog */}
      <Dialog open={remarkDialog.open} onOpenChange={(v) => { if (!v) setRemarkDialog({ open: false, qid: null, text: "" }); }}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-amber-500" /> Follow-up Remarks
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-slate-500">
              Update follow-up notes, client feedback, or reminder status:
            </p>
            <Textarea
              rows={4}
              value={remarkDialog.text}
              onChange={(e) => setRemarkDialog({ ...remarkDialog, text: e.target.value })}
              placeholder="e.g. Followed up on quotation today. Client requested delivery schedule..."
              className="rounded-none text-sm"
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRemarkDialog({ open: false, qid: null, text: "" })}
                className="rounded-none h-8 text-xs"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={saveRemarkDialog}
                className="rounded-none btn-amber font-semibold h-8 text-xs"
              >
                Save Remarks
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}